import React, { useEffect, useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso || "";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
    + " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/notifications/me?limit=100`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleClick = async (n) => {
    if (!n.read) {
      try { await axios.post(`${API}/api/notifications/${n.id}/read`, {}, { withCredentials: true }); } catch {}
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    // Any ticket-related notification → open the ticket detail for the user.
    // Admin uses /tickets, all portal roles use /users/tickets.
    if (
      (n.kind === "ticket_created" || n.kind === "ticket_reply" || n.kind === "ticket_updated") &&
      n.payload?.ticket_id
    ) {
      const target = user?.role === "admin" ? "/tickets" : "/users/tickets";
      const dispatch = () => window.dispatchEvent(
        new CustomEvent("open-triage-modal", { detail: { ticket_id: n.payload.ticket_id } })
      );
      if (window.location.pathname !== target) {
        navigate(target);
        setTimeout(dispatch, 300);
      } else {
        dispatch();
      }
    }
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch {}
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-sub">Alerts, mentions, and system updates appear here.</p>
        </div>
        {unread > 0 && (
          <button
            className="btn-ghost"
            onClick={handleMarkAll}
            data-testid="notif-page-mark-all"
          >
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <div className="table-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-card" data-testid="notifications-empty">
          <div className="coming-soon-icon-wrap">
            <Bell size={36} color="#43E0FF" />
          </div>
          <p className="coming-soon-title">No notifications yet</p>
          <p className="coming-soon-sub">
            You&apos;re all caught up. New activity will show up here as it happens.
          </p>
        </div>
      ) : (
        <div className="notif-list">
          {items.map((n) => (
            <button
              key={n.id}
              className={`notif-list-item${n.read ? "" : " notif-list-item-unread"}`}
              onClick={() => handleClick(n)}
              data-testid={`notif-list-item-${n.id}`}
            >
              <div className="notif-list-icon"><Bell size={16} /></div>
              <div className="notif-list-body">
                <div className="notif-list-title">{n.title || n.kind}</div>
                {n.body && <div className="notif-list-text">{n.body}</div>}
                <div className="notif-list-meta">
                  {n.payload?.college_name && <span>{n.payload.college_name}</span>}
                  {n.payload?.raised_by_name && <span> · {n.payload.raised_by_name}</span>}
                  <span> · {fmtTime(n.created_at)}</span>
                </div>
              </div>
              {!n.read && <span className="notif-list-dot" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
